import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Alert,
    Snackbar,
    Stack,
    Divider
} from '@mui/material';
import { Send as SendIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { sendWhistleblowerReport } from '../services/email';
import { getClientPrincipal } from '../services/auth';

interface WhistleblowerFormData {
    dateOfReport: string;
    contactDetails: string;
    natureOfConcern: string;
}

const WhistleblowerFormPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<WhistleblowerFormData>({
        dateOfReport: new Date().toISOString().split('T')[0],
        contactDetails: '',
        natureOfConcern: ''
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        // Validate required fields
        if (!formData.natureOfConcern.trim()) {
            setSnackbarMessage('Please provide details about the nature of concern or incident');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setIsSubmitting(true);

        try {
            // Get user info
            const principal = await getClientPrincipal();
            const submitterEmail = principal?.userDetails || 'Anonymous';
            const submitterName = principal?.name || 'Anonymous';

            // Send email
            const success = await sendWhistleblowerReport({
                dateOfReport: formData.dateOfReport,
                contactDetails: formData.contactDetails,
                natureOfConcern: formData.natureOfConcern,
                submitterEmail,
                submitterName
            });

            if (success) {
                setSnackbarMessage('Whistleblower report submitted successfully. Your report has been sent to whistleblow@iodparc.com');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);

                // Reset form
                setFormData({
                    dateOfReport: new Date().toISOString().split('T')[0],
                    contactDetails: '',
                    natureOfConcern: ''
                });
            } else {
                setSnackbarMessage('Failed to submit report. Please try again or contact IT support.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Error submitting whistleblower report:', error);
            setSnackbarMessage('An error occurred while submitting the report.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        // Reset form and navigate back to home
        setFormData({
            dateOfReport: new Date().toISOString().split('T')[0],
            contactDetails: '',
            natureOfConcern: ''
        });
        navigate('/');
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 3 }}>
                    Whistleblower Reporting
                </Typography>

                <Stack spacing={3}>
                    {/* Date of Report */}
                    <TextField
                        fullWidth
                        label="Date of Report"
                        type="date"
                        value={formData.dateOfReport}
                        onChange={(e) => setFormData(prev => ({ ...prev, dateOfReport: e.target.value }))}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />

                    {/* Contact Details */}
                    <TextField
                        fullWidth
                        label="Contact Details (see below)*"
                        multiline
                        rows={4}
                        value={formData.contactDetails}
                        onChange={(e) => setFormData(prev => ({ ...prev, contactDetails: e.target.value }))}
                        placeholder="Optional: Provide your contact information if you wish to be contacted"
                    />

                    {/* Nature of Concern or Incident */}
                    <TextField
                        fullWidth
                        required
                        label="Nature of Concern or Incident"
                        multiline
                        rows={8}
                        value={formData.natureOfConcern}
                        onChange={(e) => setFormData(prev => ({ ...prev, natureOfConcern: e.target.value }))}
                        placeholder="Please supply as much information as possible. This may include, but is not limited to the following information:
• Date of incident
• Location of incident
• Description of incident or concern
• Who was involved (names and job role, if known)
• Others who are aware of incident or concern & contact details
• How to contact individual reporting the concern"
                    />

                    {/* Information Box */}
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.6, fontWeight: 500 }}>
                            Please supply as much information as possible. This may include, but is not limited to the following information:
                        </Typography>
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li><Typography variant="body2">Date of incident</Typography></li>
                            <li><Typography variant="body2">Location of incident</Typography></li>
                            <li><Typography variant="body2">Description of incident or concern</Typography></li>
                            <li><Typography variant="body2">Who was involved (names and job role, if known)</Typography></li>
                            <li><Typography variant="body2">Others who are aware of incident or concern & contact details</Typography></li>
                            <li><Typography variant="body2">How to contact individual reporting the concern</Typography></li>
                        </ul>
                    </Alert>

                    {/* Disclaimer */}
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            *All concerns can be reported anonymously and will be investigated as necessary depending on the nature of the concern. 
                            If you wish to leave your contact details, this may help to better address any concerns raised and subsequent investigation.
                        </Typography>
                    </Alert>

                    <Divider sx={{ my: 2 }} />

                    {/* Whistleblower Reporting Mechanism Link */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Whistleblower reporting mechanism -{' '}
                            <a 
                                href="https://iodparcuk.sharepoint.com/sites/forms/Documents/Forms/AllItems.aspx?id=%2Fsites%2Fforms%2FDocuments%2FIOD%20PARC%20Whistleblower%20Reporting%20Mechanism%2Epdf&parent=%2Fsites%2Fforms%2FDocuments"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#009688', textDecoration: 'underline' }}
                            >
                                Click here to open document
                            </a>
                        </Typography>
                    </Box>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{
                                bgcolor: '#009688',
                                '&:hover': {
                                    bgcolor: '#00796b'
                                }
                            }}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default WhistleblowerFormPage;
